const modalRegistry = new Map();
let scrollY = 0;
let openModal = null;

const samePageHashFromLink = (link) => {
  const href = link.getAttribute("href");

  if (!href || !href.includes("#")) return "";

  const url = new URL(href, window.location.href);

  if (url.origin !== window.location.origin) return "";
  if (url.pathname !== window.location.pathname) return "";
  if (url.search !== window.location.search) return "";

  return decodeURIComponent(url.hash.slice(1));
};

const lockScroll = () => {
  scrollY = window.scrollY;
  document.body.style.width = "100%";
  document.body.style.position = "fixed";
  document.body.style.top = `-${scrollY}px`;
};

const unlockScroll = () => {
  document.body.style.width = "";
  document.body.style.position = "";
  document.body.style.top = "";
  window.scrollTo({ top: scrollY, behavior: "instant" });
};

const updateModalViewportHeight = () => {
  const visualViewport = window.visualViewport;
  const viewportHeight = visualViewport?.height || window.innerHeight;
  const viewportOffsetTop = visualViewport?.offsetTop || 0;

  document.documentElement.style.setProperty(
    "--lp-modal-viewport-height",
    `${viewportHeight}px`,
  );
  document.documentElement.style.setProperty(
    "--lp-modal-viewport-offset-top",
    `${viewportOffsetTop}px`,
  );
};

const keepFocusedFieldVisible = (dialog) => {
  const activeElement = document.activeElement;

  if (!(dialog instanceof HTMLElement)) return;
  if (!(activeElement instanceof HTMLElement)) return;
  if (!dialog.contains(activeElement)) return;

  window.requestAnimationFrame(() => {
    activeElement.scrollIntoView({
      block: "center",
      inline: "nearest",
    });
  });
};

const onAnimationEnd = (element) =>
  new Promise((resolve) => {
    const styles = window.getComputedStyle(element);

    if (styles.animationName === "none" || styles.animationDuration === "0s") {
      resolve();
      return;
    }

    element.addEventListener("animationend", resolve, { once: true });
  });

const isPointInDialog = (event, dialog) => {
  const dialogRect = dialog.getBoundingClientRect();

  return (
    event.clientX >= dialogRect.left &&
    event.clientX <= dialogRect.right &&
    event.clientY >= dialogRect.top &&
    event.clientY <= dialogRect.bottom
  );
};

class LPModal extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector("[data-lp-modal-dialog]");
    this.openButton = this.querySelector("[data-lp-modal-open]");
    this.modalId = this.dataset.modalId || this.dialog?.id;

    if (!this.dialog || !this.modalId) return;

    modalRegistry.set(this.modalId, this);

    this.openButton?.addEventListener("click", this.open);
    this.dialog.addEventListener("cancel", this.handleCancel);
    this.dialog.addEventListener("close", this.handleNativeClose);
    this.dialog.addEventListener("focusin", this.handleDialogFocusIn);
    this.querySelectorAll("[data-lp-modal-close]").forEach((button) => {
      button.addEventListener("click", this.close);
    });

    openMatchingInitialHash();
  }

  disconnectedCallback() {
    if (this.modalId) modalRegistry.delete(this.modalId);

    this.openButton?.removeEventListener("click", this.open);
    document.removeEventListener(
      "pointerdown",
      this.handleDocumentPointerDown,
      true,
    );
    document.removeEventListener(
      "pointerup",
      this.handleDocumentPointerUp,
      true,
    );
    this.dialog?.removeEventListener("cancel", this.handleCancel);
    this.dialog?.removeEventListener("close", this.handleNativeClose);
    this.dialog?.removeEventListener("focusin", this.handleDialogFocusIn);
    this.querySelectorAll("[data-lp-modal-close]").forEach((button) => {
      button.removeEventListener("click", this.close);
    });
  }

  open = async () => {
    if (!this.dialog || this.dialog.open) return;

    if (openModal && openModal !== this) {
      await openModal.close();
    }

    this.previouslyFocusedElement = document.activeElement;
    openModal = this;
    lockScroll();
    updateModalViewportHeight();
    this.dialog.showModal();
    window.addEventListener("resize", this.handleViewportResize);
    window.visualViewport?.addEventListener(
      "resize",
      this.handleViewportResize,
    );
    document.addEventListener(
      "pointerdown",
      this.handleDocumentPointerDown,
      true,
    );
    document.addEventListener("pointerup", this.handleDocumentPointerUp, true);
  };

  close = async () => {
    if (!this.dialog?.open) return;

    this.dialog.classList.add("lp-modal__dialog--closing");
    await onAnimationEnd(this.dialog);

    this.dialog.close();
  };

  handleDocumentPointerDown = (event) => {
    this.backdropPointerDown = false;

    if (!this.dialog?.open) return;
    if (isPointInDialog(event, this.dialog)) return;

    this.backdropPointerDown = true;
    event.preventDefault();
  };

  handleDocumentPointerUp = (event) => {
    if (!this.dialog?.open) return;
    if (!this.backdropPointerDown) return;

    this.backdropPointerDown = false;
    event.preventDefault();

    if (!isPointInDialog(event, this.dialog)) this.close();
  };

  handleCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  handleViewportResize = () => {
    updateModalViewportHeight();
    keepFocusedFieldVisible(this.dialog);
  };

  handleDialogFocusIn = () => {
    keepFocusedFieldVisible(this.dialog);
  };

  handleNativeClose = () => {
    this.dialog.classList.remove("lp-modal__dialog--closing");
    window.removeEventListener("resize", this.handleViewportResize);
    window.visualViewport?.removeEventListener(
      "resize",
      this.handleViewportResize,
    );
    document.removeEventListener(
      "pointerdown",
      this.handleDocumentPointerDown,
      true,
    );
    document.removeEventListener(
      "pointerup",
      this.handleDocumentPointerUp,
      true,
    );

    if (openModal === this) {
      openModal = null;
      unlockScroll();
    }

    if (this.previouslyFocusedElement instanceof HTMLElement) {
      this.previouslyFocusedElement.focus();
    }
  };
}

if (!customElements.get("lp-modal")) {
  customElements.define("lp-modal", LPModal);
}

document.addEventListener("click", (event) => {
  const link =
    event.target instanceof Element ? event.target.closest("a[href]") : null;
  if (!link) return;

  const modalId = samePageHashFromLink(link);
  const modal = modalRegistry.get(modalId);

  if (!modal) return;

  event.preventDefault();
  modal.open();
});

const openMatchingInitialHash = () => {
  const modalId = decodeURIComponent(window.location.hash.slice(1));
  const modal = modalRegistry.get(modalId);

  if (modal && !modal.dialog.open) {
    window.requestAnimationFrame(() => modal.open());
  }
};

window.addEventListener("hashchange", openMatchingInitialHash);

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", openMatchingInitialHash, {
    once: true,
  });
} else {
  openMatchingInitialHash();
}
